import { conversationsQueryKeys } from 'api/consts'
import { queryClient } from 'contexts/reactQuery'

import { Conversation, Message } from 'types/Api'

import type { UpdateMessageStreamingResponse } from 'api/messages/types'

import type {
  FetchConversationResponse,
  InfinityConversationsResponse,
  UpdateConversationResponse,
  CreateConversationResponse,
} from './types'

const initialConversationsData = {
  pageParams: [],
  pages: [
    {
      conversations: [],
      expiringCount: 0,
      pagination: {
        currentPage: 1,
        numberOfHits: 0,
        pages: 1,
        perPage: 1,
      },
      pinned: [],
    },
  ],
} as InfinityConversationsResponse

export const addNewConversationQueryClient = (
  conversation: CreateConversationResponse['conversation'],
) => {
  queryClient.setQueryData<CreateConversationResponse>(
    conversationsQueryKeys.conversationDetails(conversation.id),
    { conversation },
  )

  queryClient.setQueryData<InfinityConversationsResponse>(
    conversationsQueryKeys.conversationsList(),
    // @ts-expect-error - We currently save the newly created conversation with empty messages array
    // in the sidebar, that normally doesn't have that from the api.
    (cachedConversations) => {
      const data = cachedConversations ?? initialConversationsData

      const firstPage = data?.pages[0]

      return {
        ...data,
        pages: [
          {
            ...firstPage,
            conversations: [...firstPage.conversations, conversation],
          },
          ...data.pages.slice(0, 0),
        ],
      }
    },
  )
}

export const addMessagesToConversationQueryClient = (
  conversationId: string,
  messages: Message[],
) =>
  queryClient.setQueryData<FetchConversationResponse>(
    conversationsQueryKeys.conversationDetails(conversationId),
    (prevConversation) => {
      if (!prevConversation) {
        return undefined
      }

      return {
        conversation: {
          ...prevConversation.conversation,
          messages: [...prevConversation.conversation.messages, ...messages],
        },
      }
    },
  )

export const removeMessagesFromConversationQueryClient = (
  conversationId: string,
  messageId: string,
) =>
  queryClient.setQueryData<FetchConversationResponse>(
    conversationsQueryKeys.conversationDetails(conversationId),
    (cachedConversationData) => {
      if (!cachedConversationData) return undefined

      const index = cachedConversationData.conversation.messages.findIndex(
        message => message.id === messageId,
      )

      if (index === -1) {
        return cachedConversationData
      }

      return {
        conversation: {
          ...cachedConversationData.conversation,
          messages: [
            ...cachedConversationData.conversation.messages.slice(0, index),
          ],
        },
      }
    },
  )

export const updateConversationMessageFeedback = ({
  conversationId,
  messageId,
  feedback,
}: {
  conversationId: string
  messageId: string
  feedback: Message['feedback']
}) =>
  queryClient.setQueryData<FetchConversationResponse>(
    conversationsQueryKeys.conversationDetails(conversationId),
    (conversation) => {
      if (!conversation) return undefined

      const updatedMessages = conversation.conversation.messages.map(
        (message) => {
          if (message.id === messageId) {
            return {
              ...message,
              feedback,
            }
          }

          return message
        },
      )

      return {
        conversation: {
          ...conversation.conversation,
          messages: updatedMessages,
        },
      }
    },
  )

const updateConversationByIdQueryClient = (
  updateConversationResponse: UpdateConversationResponse,
) =>
  queryClient.setQueryData<FetchConversationResponse>(
    conversationsQueryKeys.conversationDetails(updateConversationResponse.id),
    (prevConversation) => {
      if (!prevConversation) return undefined

      const updatedConversation = {
        ...prevConversation.conversation,
        ...updateConversationResponse,
      }

      return { conversation: updatedConversation }
    },
  )

export const updateConversationSubjectByIdQueryClient = (
  conversation: UpdateConversationResponse,
  query: string,
) => {
  updateConversationByIdQueryClient(conversation)

  queryClient.setQueryData<InfinityConversationsResponse>(
    conversationsQueryKeys.conversationsList(query),
    (data) => {
      if (!data) return undefined

      const updateConversationList = (
        conversationsList: Conversation[],
      ) =>
        conversationsList.map(c =>
          c.id === conversation.id ? conversation : c,
        )

      return {
        ...data,
        pages: data.pages.map(page => ({
          ...page,
          conversations: updateConversationList(page.conversations),
          pinned: updateConversationList(page.pinned),
        })),
      }
    },
  )
}

export const updateConversationIdPinnedQueryClient = (
  conversation: UpdateConversationResponse,
  query: string,
) => {
  updateConversationByIdQueryClient(conversation)

  queryClient.setQueryData<InfinityConversationsResponse>(
    conversationsQueryKeys.conversationsList(query),
    (data) => {
      if (!data) return undefined

      const isConversationPinned = data.pages[0].pinned.some(
        c => c.id === conversation.id,
      )

      const removeConversationFromList = (
        conversationsList: Conversation[],
      ) => conversationsList.filter(c => c.id !== conversation.id)

      const removedPinnedConversation = removeConversationFromList(
        data.pages[0].pinned,
      )

      return {
        ...data,
        pages: data.pages.map((page, index) => {
          const removedUnpinnedConversation = removeConversationFromList(
            page.conversations,
          )
          return {
            ...page,
            conversations:
              index === 0 && isConversationPinned
                ? [...removedUnpinnedConversation, conversation]
                : removedUnpinnedConversation,
            pinned:
              index === 0 && !isConversationPinned
                ? [...removedPinnedConversation, conversation]
                : removedPinnedConversation,
          }
        }),
      }
    },
  )
}

export const updateMessageQueryClient = ({
  message,
}: UpdateMessageStreamingResponse) =>
  queryClient.setQueryData<FetchConversationResponse>(
    conversationsQueryKeys.conversationDetails(message.conversationId),
    (conversation) => {
      if (!conversation) return undefined

      return {
        ...conversation,
        conversation: {
          ...conversation.conversation,
          messages: [
            ...conversation.conversation.messages.map((conversationMessage) => {
              if (conversationMessage.id === message.id) {
                return message
              }
              return conversationMessage
            }),
          ],
        },
      }
    },
  )

type UpdateMessageStatusInput = {
  conversationId: string
  messageId: string
  status: Message['status']
}

export const updateMessageStatusQueryClient = ({
  conversationId,
  messageId,
  status,
}: UpdateMessageStatusInput) =>
  queryClient.setQueryData<FetchConversationResponse>(
    conversationsQueryKeys.conversationDetails(conversationId),
    (cachedConversationData) => {
      if (!cachedConversationData) return undefined

      return {
        conversation: {
          ...cachedConversationData.conversation,
          messages: [
            ...cachedConversationData.conversation.messages.map((m) => {
              if (m.id === messageId) {
                return { ...m, status }
              }
              return m
            }),
          ],
        },
      }
    },
  )

export const updateConversationMetadata = (
  conversationMetadata: Conversation,
) => {
  queryClient.setQueryData<FetchConversationResponse>(
    conversationsQueryKeys.conversationDetails(conversationMetadata.id),
    (cachedConversationData) => {
      if (!cachedConversationData) return undefined

      return {
        conversation: {
          ...cachedConversationData.conversation,
          ...conversationMetadata,
        },
      }
    },
  )

  queryClient.setQueryData<InfinityConversationsResponse>(
    conversationsQueryKeys.conversationsList(),
    (cachedConversations) => {
      const data = cachedConversations ?? initialConversationsData

      const firstPage = data?.pages[0]

      return {
        ...data,
        pages: [
          {
            ...firstPage,
            conversations: [
              ...firstPage.conversations.map((conversation) => {
                if (conversation.id === conversationMetadata.id) {
                  return conversationMetadata
                }

                return conversation
              }),
            ],
          },
          ...data.pages.slice(0, 0),
        ],
      }
    },
  )
}
